import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitCart } from '@/api/cart';
import { useCart } from '@/hooks/useCart';
import { alertError, showConfirm } from '@/utils/message';

/** 表头与数据行共用列宽，保证对齐 */
const CART_COLS =
  'grid grid-cols-1 gap-3 md:grid-cols-[4rem_minmax(12rem,1fr)_11rem_4rem_6rem_7rem_6rem_4rem] md:gap-x-3 md:gap-y-0 md:items-center';

const REMARK_MAX = 255;

export default function Cart() {
  const navigate = useNavigate();
  const { items, removeItem, removeLines, updateQuantity, updateRemark, clearCart } = useCart();
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set(items.map((_, i) => i)));

  // Form state
  const [purchaseRemark, setPurchaseRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 商品列表变化时，校正选中下标（避免越界；清空选中时不强制全选）
  useEffect(() => {
    setSelectedItems((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx >= 0 && idx < items.length) next.add(idx);
      });
      // 内容未变则复用原 Set，避免多余渲染
      if (next.size === prev.size) {
        let same = true;
        prev.forEach((idx) => {
          if (!next.has(idx)) same = false;
        });
        if (same) return prev;
      }
      return next;
    });
  }, [items]);

  const toggleSelect = (index: number) => {
    const next = new Set(selectedItems);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setSelectedItems(next);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === items.length && items.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((_, i) => i)));
    }
  };

  const selectedTotal = items
    .filter((_, i) => selectedItems.has(i))
    .reduce((sum, item) => sum + item.price * item.quantity, 0);

  const selectedCount = items
    .filter((_, i) => selectedItems.has(i))
    .reduce((sum, item) => sum + item.quantity, 0);

  const validate = () => {
    const next: Record<string, string> = {};
    if (purchaseRemark.length > REMARK_MAX) {
      next.purchaseRemark = `备注不能超过${REMARK_MAX}字`;
    }
    if (selectedCount === 0) {
      next.items = '请至少选择一件商品';
    }

    const invalidSpec = items
      .filter((_, i) => selectedItems.has(i))
      .find((item) => {
        const specId = Number(item.specId || item.productId);
        return !Number.isFinite(specId) || specId <= 0;
      });
    if (invalidSpec) {
      next.items = `商品「${invalidSpec.name}」缺少有效规格，请删除后重新加入`;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || submitting) return;

    const selectedList = items.filter((_, i) => selectedItems.has(i));
    const isPartial = selectedList.length < items.length;

    setSubmitting(true);
    try {
      const missingSpec = selectedList.find((item) => !item.specId);
      if (missingSpec) {
        await alertError(
          `「${missingSpec.name}」缺少规格信息，请从商品详情重新加入购物车后再提交`,
          '提交采购单',
        );
        return;
      }

      const res = await submitCart({
        specs: selectedList.map((item) => ({
          id: Number(item.specId),
          name: item.specName || item.model || '',
          goods_id: Number(item.productId),
          goods_name: item.name,
          quantity: item.quantity,
          remark: item.remark || '',
        })),
        remark: purchaseRemark.trim() || undefined,
      });

      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        await alertError(res.message || res.msg || '提交失败，请稍后重试', '提交采购单');
        return;
      }

      const submittedLines = selectedList.map((item) => ({
        productId: item.productId,
        specId: item.specId,
      }));

      if (isPartial) {
        removeLines(submittedLines);
        setPurchaseRemark('');
        setErrors({});
        const goOrders = await showConfirm(
          '采购单提交成功。购物车中仍有未提交商品，是否前往订单列表查看？',
          {
            title: '提交成功',
            confirmLabel: '前往订单',
            cancelLabel: '留在购物车',
          },
        );
        if (goOrders) {
          navigate('/orders');
        }
      } else {
        clearCart();
        setSuccess(true);
      }
    } catch {
      await alertError('网络异常，提交失败，请稍后重试', '提交采购单');
    } finally {
      setSubmitting(false);
    }
  };

  const actionBar = (
    <div className="px-5 py-3 flex items-center justify-between">
      <span className="text-sm text-foreground-500">
        已选 <strong className="text-foreground-900">{selectedCount}</strong> 件 · 合计{' '}
        <strong className="text-primary-600">¥{selectedTotal.toFixed(2)}</strong>
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/goods')}
          className="px-4 py-1.5 rounded-md border border-background-300 text-xs text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
        >
          继续选购
        </button>
        <button
          type="button"
          onClick={clearCart}
          className="px-4 py-1.5 rounded-md border border-primary-200 text-xs text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
        >
          清空购物车
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {submitting ? (
            <span className="flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" />
              提交中...
            </span>
          ) : (
            '提交采购单'
          )}
        </button>
      </div>
    </div>
  );

  if (items.length === 0 && !success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 bg-background-200 rounded-full flex items-center justify-center mb-4">
          <i className="ri-shopping-cart-line text-3xl text-foreground-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">购物车是空的</h2>
        <p className="text-sm text-foreground-500 mb-6">快去挑选您需要的物资吧</p>
        <button
          onClick={() => navigate('/goods')}
          className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm transition-colors whitespace-nowrap"
        >
          去逛逛
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mb-4">
          <i className="ri-check-line text-3xl text-accent-600" />
        </div>
        <h2 className="text-xl font-semibold text-foreground-900 mb-2">采购单提交成功</h2>
        <p className="text-sm text-foreground-500 mb-2 text-center">您的采购申请已提交，系统将自动流转审批</p>
        <p className="text-xs text-foreground-400 mb-6">可在"我的订单"中查看审批进度</p>
        <div className="flex flex-row gap-3">
          <button
            onClick={() => navigate('/orders')}
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm transition-colors whitespace-nowrap"
          >
            查看我的订单
          </button>
          <button
            onClick={() => navigate('/goods')}
            className="px-6 py-2.5 border border-background-300 text-foreground-600 rounded-lg text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            继续采购
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6">
        <form onSubmit={handleSubmit} id="cart-form">
          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">

            {/* Card Header */}
            <div className="border-b border-background-200">{actionBar}</div>

            {/* Card Body */}
            <div className="p-5 overflow-x-auto">

              {/* Header row */}
              <div className={`${CART_COLS} bg-[#f8f8f9] rounded-lg px-4 py-2.5 mb-3 text-xs text-foreground-600 font-medium border border-background-200 min-w-[56rem]`}>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 whitespace-nowrap"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedItems.size === items.length && items.length > 0 ? 'bg-primary-500 border-primary-500' : 'border-background-300'}`}>
                    {selectedItems.size === items.length && items.length > 0 && <i className="ri-check-line text-background-50 text-xs" />}
                  </div>
                  全选
                </button>
                <span>商品信息</span>
                <span className="text-center">规格型号</span>
                <span className="text-center">单位</span>
                <span className="text-center">单价</span>
                <span className="text-center">数量</span>
                <span className="text-center">小计</span>
                <span className="text-center">操作</span>
              </div>

              {/* Items */}
              <div className="space-y-3 min-w-[56rem]">
                {items.map((item, idx) => (
                  <div
                    key={`${item.productId}-${item.specId || 'default'}`}
                    className="bg-background-50 rounded-lg border border-background-200 px-4 py-3"
                  >
                    <div className={CART_COLS}>
                      <button
                        type="button"
                        onClick={() => toggleSelect(idx)}
                        className="flex items-center"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedItems.has(idx) ? 'bg-primary-500 border-primary-500' : 'border-background-300'}`}>
                          {selectedItems.has(idx) && <i className="ri-check-line text-background-50 text-xs" />}
                        </div>
                      </button>

                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-16 h-16 bg-background-100 rounded-lg overflow-hidden shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-relaxed min-w-0">
                          {item.name}
                        </h3>
                      </div>

                      <div className="text-center text-xs text-foreground-600 leading-relaxed break-words px-1">
                        {item.specName || item.model || '—'}
                      </div>

                      <div className="text-center text-xs text-foreground-600">
                        {item.unit}
                      </div>

                      <div className="text-center text-sm text-foreground-700">
                        ¥{item.price.toFixed(2)}
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="flex items-center border border-background-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={(e) =>
                              updateQuantity(item.productId, item.quantity - 1, {
                                anchor: e.currentTarget,
                              })
                            }
                            className="w-8 h-8 flex items-center justify-center hover:bg-background-100 transition-colors text-foreground-600"
                          >
                            <i className="ri-subtract-line" />
                          </button>
                          <input
                            type="number"
                            min={1}
                            max={item.stock >= 0 ? item.stock : undefined}
                            value={item.quantity}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') return;
                              const n = parseInt(raw, 10);
                              updateQuantity(item.productId, Number.isFinite(n) ? n : 0, {
                                anchor: e.currentTarget,
                              });
                            }}
                            className="w-10 h-8 text-center text-sm outline-none border-x border-background-300"
                          />
                          <button
                            type="button"
                            onClick={(e) =>
                              updateQuantity(item.productId, item.quantity + 1, {
                                anchor: e.currentTarget,
                              })
                            }
                            className="w-8 h-8 flex items-center justify-center hover:bg-background-100 transition-colors text-foreground-600"
                          >
                            <i className="ri-add-line" />
                          </button>
                        </div>
                      </div>

                      <div className="text-center text-sm font-semibold text-primary-600">
                        ¥{(item.price * item.quantity).toFixed(2)}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item.productId)}
                        className="flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors"
                      >
                        <i className="ri-delete-bin-line text-lg" />
                      </button>
                    </div>

                    {/* Remark */}
                    <div className="mt-3 md:pl-[calc(4rem+0.75rem)]">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground-500 shrink-0 whitespace-nowrap">商品备注：</span>
                        <input
                          type="text"
                          placeholder="填写商品备注说明"
                          value={item.remark || ''}
                          onChange={(e) => updateRemark(item.productId, e.target.value)}
                          maxLength={200}
                          className="flex-1 px-3 py-1.5 text-xs border border-background-300 rounded-md outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        />
                        {item.remark && (
                          <span className="text-xs text-foreground-400 shrink-0">{item.remark.length}/200</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Purchase Remark */}
              <div className="mt-5 pt-4 border-t border-background-200">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-foreground-700">采购备注</label>
                  <span className={`text-[11px] ${purchaseRemark.length > REMARK_MAX ? 'text-primary-600' : 'text-foreground-400'}`}>
                    {purchaseRemark.length}/{REMARK_MAX}
                  </span>
                </div>
                <textarea
                  value={purchaseRemark}
                  onChange={(e) => setPurchaseRemark(e.target.value.slice(0, REMARK_MAX))}
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg border text-xs outline-none resize-none transition-colors ${errors.purchaseRemark ? 'border-primary-400 bg-primary-50' : 'border-background-300 focus:border-accent-400'}`}
                  placeholder="请填写采购用途、紧急程度等备注信息（选填）"
                />
                {errors.purchaseRemark && <p className="text-xs text-primary-600 mt-1">{errors.purchaseRemark}</p>}

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {errors.items && <p className="text-xs text-primary-600 mt-2">{errors.items}</p>}
              </div>
            </div>

            {/* Card Footer：超过 5 条时复用操作栏，方便底部提交 */}
            {items.length > 5 && (
              <div className="border-t border-background-200">{actionBar}</div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
